import {
  createAppError,
  getAppErrorCause,
  isAppError,
} from '@bangle.io/base-utils';
import { isVisibleWorkspaceFilePath, WsPath } from '@bangle.io/ws-path';

export const WORKSPACE_MANIFEST_NAME = 'sariz-notes.json';

const MARKDOWN_EXT = /\.(md|markdown|mdown|mkd|txt|fountain)$/i;
const ZIP_EXT = /\.zip$/i;
const SKIP_ZIP_PATH =
  /(^|\/)(\.DS_Store|__MACOSX|Thumbs\.db|desktop\.ini)(\/|$)/i;

export type WorkspaceImportExportFileSystem = {
  listWorkspaceFiles: (wsName: string) => Promise<string[]>;
  readFile: (wsPath: string) => Promise<File | undefined>;
  createFile: (wsPath: string, file: File) => Promise<void>;
};

export type WorkspaceManifest = {
  version: 1;
  title: string;
  exportedAt: string;
  files: string[];
};

export type ExportWorkspaceResult = {
  fileCount: number;
  filename: string;
  blob: Blob;
};

export type ImportWorkspaceResult = {
  imported: number;
  skipped: number;
  renamed: number;
};

export type ImportProgress = {
  completed: number;
  total: number;
  /** Relative path or file name currently being processed, when known. */
  currentFile?: string;
};

export type ImportProgressCallback = (progress: ImportProgress) => void;

export type ExportProgress = {
  completed: number;
  total: number;
  /** Relative path or archive name currently being processed, when known. */
  currentFile?: string;
};

export type ExportProgressCallback = (progress: ExportProgress) => void;

const PROGRESS_YIELD_MS = 50;

/**
 * Notify progress and periodically yield so React can paint. Near-instant
 * IndexedDB writes otherwise batch every update into one frame that is then
 * cleared in `finally`, so the progress UI never appears.
 */
async function reportProgress(
  onProgress: ((progress: ImportProgress) => void) | undefined,
  progress: ImportProgress,
  state: { lastYieldAt: number },
  options?: { force?: boolean },
): Promise<void>;
async function reportProgress(
  onProgress: ((progress: ExportProgress) => void) | undefined,
  progress: ExportProgress,
  state: { lastYieldAt: number },
  options?: { force?: boolean },
): Promise<void>;
async function reportProgress(
  onProgress:
    | ((progress: { completed: number; total: number }) => void)
    | undefined,
  progress: ImportProgress | ExportProgress,
  state: { lastYieldAt: number },
  options?: { force?: boolean },
): Promise<void> {
  onProgress?.(progress);
  if (!onProgress) {
    return;
  }
  const now =
    typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (!options?.force && now - state.lastYieldAt < PROGRESS_YIELD_MS) {
    return;
  }
  state.lastYieldAt = now;
  await new Promise<void>((resolve) => {
    globalThis.setTimeout(resolve, 0);
  });
}

export function isMarkdownFile(file: File | { name?: string }): boolean {
  const name = fileNameOf(file);
  return Boolean(name && MARKDOWN_EXT.test(name));
}

export function isZipFile(
  file: File | { name?: string; type?: string },
): boolean {
  const name = fileNameOf(file);
  if (!name) {
    return false;
  }
  if (ZIP_EXT.test(name)) {
    return true;
  }
  const type = String(file.type || '').toLowerCase();
  return type === 'application/zip' || type === 'application/x-zip-compressed';
}

export function isImportableFile(file: File): boolean {
  return isMarkdownFile(file) || isZipFile(file);
}

function fileNameOf(file: { name?: string } | File): string {
  if (!file) {
    return '';
  }
  if (typeof file.name === 'string' && file.name) {
    return file.name;
  }
  // Vitest's File polyfill stores the name on `filename`.
  const filename = (file as { filename?: string }).filename;
  return typeof filename === 'string' ? filename : '';
}

export function slugifyFilename(name: string): string {
  const normalized = String(name || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return normalized || 'workspace';
}

export function downloadBlobFile(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so the download can start first.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function stripLeadingSlash(path: string): string {
  return path.replace(/^\/+/, '');
}

async function readFileBytes(file: File): Promise<Uint8Array> {
  const parts = (file as { parts?: unknown[] }).parts;
  const part = parts?.[0];
  if (part instanceof Uint8Array) {
    return part;
  }
  if (part instanceof ArrayBuffer) {
    return new Uint8Array(part);
  }
  if (typeof Blob !== 'undefined' && part instanceof Blob) {
    return new Uint8Array(await part.arrayBuffer());
  }
  if (typeof file.arrayBuffer === 'function') {
    try {
      return new Uint8Array(await file.arrayBuffer());
    } catch {
      // Fall through to text() for polyfilled Files with string parts.
    }
  }
  return new TextEncoder().encode(await file.text());
}

function normalizeImportRelativePath(rawPath: string): string | undefined {
  const cleaned = stripLeadingSlash(
    String(rawPath || '')
      .replace(/\\/g, '/')
      .trim(),
  );
  if (!cleaned || SKIP_ZIP_PATH.test(cleaned)) {
    return undefined;
  }
  if (cleaned.split('/').some((segment) => segment === '..')) {
    return undefined;
  }
  // Manifest is metadata only; content is restored from the zip entries.
  if (cleaned === WORKSPACE_MANIFEST_NAME) {
    return undefined;
  }
  return cleaned;
}

/**
 * Markdown-like imports become first-class `.md` notes. Other extensions keep
 * their names so assets round-trip with relative links intact.
 */
export function normalizeImportedFileName(fileName: string): string {
  const trimmed = fileName.trim();
  if (!trimmed) {
    return 'untitled.md';
  }
  if (/\.(markdown|mdown|mkd|txt|fountain)$/i.test(trimmed)) {
    return `${trimmed.replace(/\.[^.]+$/, '')}.md`;
  }
  if (/\.md$/i.test(trimmed)) {
    return trimmed.replace(/\.md$/i, '.md');
  }
  return trimmed;
}

function relativePathFromWsPath(wsPath: string): string | undefined {
  const filePath = WsPath.safeParseFile(wsPath).data;
  if (!filePath || !isVisibleWorkspaceFilePath(filePath)) {
    return undefined;
  }
  return filePath.path;
}

async function createFileWithoutOverwrite(
  fileSystem: WorkspaceImportExportFileSystem,
  wsPath: string,
  file: File,
): Promise<{ wsPath: string; renamed: boolean }> {
  const base = WsPath.assertFile(wsPath);
  for (let attempt = 1; attempt <= 100; attempt += 1) {
    const destination =
      attempt === 1
        ? base
        : base.replaceFileName(
            `${base.fileNameWithoutExtension}-${attempt}${base.extension}`,
          );
    try {
      await fileSystem.createFile(destination.wsPath, file);
      return { wsPath: destination.wsPath, renamed: attempt > 1 };
    } catch (cause) {
      if (isAppError(cause)) {
        const appError = getAppErrorCause(cause);
        if (appError?.name === 'error::file:already-existing') {
          continue;
        }
      }
      throw cause;
    }
  }

  throw createAppError(
    'error::file:already-existing',
    'Unable to import file without overwriting an existing path',
    { wsPath },
  );
}

export async function buildWorkspaceExportZip({
  wsName,
  fileSystem,
  signal,
  onProgress,
}: {
  wsName: string;
  fileSystem: WorkspaceImportExportFileSystem;
  signal?: AbortSignal;
  onProgress?: ExportProgressCallback;
}): Promise<ExportWorkspaceResult> {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const progressYield = { lastYieldAt: 0 };
  const filename = `${slugifyFilename(wsName)}.zip`;
  await reportProgress(onProgress, { completed: 0, total: 1 }, progressYield, {
    force: true,
  });
  const wsPaths = await fileSystem.listWorkspaceFiles(wsName);
  // One step per source file plus one for compressing the archive.
  const total = wsPaths.length + 1;
  const files: string[] = [];

  for (let index = 0; index < wsPaths.length; index += 1) {
    const wsPath = wsPaths[index];
    if (!wsPath) {
      continue;
    }
    if (signal?.aborted) {
      throw signal.reason ?? new Error('Export aborted');
    }
    const relativePath = relativePathFromWsPath(wsPath);
    const currentFile = relativePath ?? wsPath;
    await reportProgress(
      onProgress,
      { completed: index, total, currentFile },
      progressYield,
    );
    if (!relativePath) {
      await reportProgress(
        onProgress,
        { completed: index + 1, total, currentFile },
        progressYield,
      );
      continue;
    }
    const file = await fileSystem.readFile(wsPath);
    if (!file) {
      await reportProgress(
        onProgress,
        { completed: index + 1, total, currentFile },
        progressYield,
      );
      continue;
    }
    const data = await readFileBytes(file);
    zip.file(relativePath, data);
    files.push(relativePath);
    await reportProgress(
      onProgress,
      { completed: index + 1, total, currentFile },
      progressYield,
    );
  }

  const manifest: WorkspaceManifest = {
    version: 1,
    title: wsName,
    exportedAt: new Date().toISOString(),
    files,
  };
  zip.file(WORKSPACE_MANIFEST_NAME, JSON.stringify(manifest, null, 2));

  const blob = await zip.generateAsync(
    { type: 'blob' },
    (metadata: { percent?: number; currentFile?: string | null }) => {
      const percent =
        typeof metadata.percent === 'number' &&
        Number.isFinite(metadata.percent)
          ? Math.min(100, Math.max(0, metadata.percent))
          : 0;
      onProgress?.({
        completed: wsPaths.length + percent / 100,
        total,
        currentFile: metadata.currentFile || filename,
      });
    },
  );
  await reportProgress(
    onProgress,
    { completed: total, total, currentFile: filename },
    progressYield,
    { force: true },
  );
  return { fileCount: files.length, filename, blob };
}

export async function exportWorkspaceAsZip({
  wsName,
  fileSystem,
  signal,
  download = true,
  onProgress,
}: {
  wsName: string;
  fileSystem: WorkspaceImportExportFileSystem;
  signal?: AbortSignal;
  download?: boolean;
  onProgress?: ExportProgressCallback;
}): Promise<ExportWorkspaceResult> {
  const result = await buildWorkspaceExportZip({
    wsName,
    fileSystem,
    signal,
    onProgress,
  });
  if (download) {
    downloadBlobFile(result.filename, result.blob);
  }
  return result;
}

async function importRelativeEntry({
  wsName,
  relativePath,
  file,
  fileSystem,
}: {
  wsName: string;
  relativePath: string;
  file: File;
  fileSystem: WorkspaceImportExportFileSystem;
}): Promise<'imported' | 'renamed' | 'skipped'> {
  const normalizedPath = normalizeImportRelativePath(relativePath);
  if (!normalizedPath) {
    return 'skipped';
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  const rawName = segments.at(-1);
  if (!rawName) {
    return 'skipped';
  }
  const fileName = normalizeImportedFileName(rawName);
  const parentPath = segments.slice(0, -1).join('/');
  const path = parentPath ? `${parentPath}/${fileName}` : fileName;

  const parsed = WsPath.safeParseFile(WsPath.fromParts(wsName, path).wsPath);
  if (!parsed.ok || !parsed.data || !isVisibleWorkspaceFilePath(parsed.data)) {
    return 'skipped';
  }

  const result = await createFileWithoutOverwrite(
    fileSystem,
    parsed.data.wsPath,
    new File([file], fileName, {
      type: file.type || 'application/octet-stream',
    }),
  );
  return result.renamed ? 'renamed' : 'imported';
}

async function importZipFile({
  wsName,
  file,
  fileSystem,
  signal,
  onProgress,
  progressOffset = 0,
  progressRemainingAfter = 0,
  progressYield,
}: {
  wsName: string;
  file: File;
  fileSystem: WorkspaceImportExportFileSystem;
  signal?: AbortSignal;
  onProgress?: ImportProgressCallback;
  progressOffset?: number;
  progressRemainingAfter?: number;
  progressYield?: { lastYieldAt: number };
}): Promise<ImportWorkspaceResult & { processed: number }> {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(await readFileBytes(file));
  let imported = 0;
  let skipped = 0;
  let renamed = 0;
  const yieldState = progressYield ?? { lastYieldAt: 0 };

  const entries = Object.values(zip.files).filter((entry) => !entry.dir);
  const total = progressOffset + entries.length + progressRemainingAfter;
  await reportProgress(
    onProgress,
    {
      completed: progressOffset,
      total,
      currentFile: fileNameOf(file) || undefined,
    },
    yieldState,
    { force: true },
  );

  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index];
    if (!entry) {
      continue;
    }
    if (signal?.aborted) {
      throw signal.reason ?? new Error('Import aborted');
    }
    const relativePath = normalizeImportRelativePath(entry.name);
    const currentFile = relativePath ?? entry.name;
    await reportProgress(
      onProgress,
      {
        completed: progressOffset + index,
        total,
        currentFile,
      },
      yieldState,
    );
    if (!relativePath) {
      skipped += 1;
      await reportProgress(
        onProgress,
        {
          completed: progressOffset + index + 1,
          total,
          currentFile,
        },
        yieldState,
      );
      continue;
    }
    const blob = await entry.async('blob');
    const fileName = relativePath.split('/').at(-1) ?? 'file';
    const importedFile = new File([blob], fileName, {
      type: blob.type || 'application/octet-stream',
    });
    const outcome = await importRelativeEntry({
      wsName,
      relativePath,
      file: importedFile,
      fileSystem,
    });
    if (outcome === 'imported') {
      imported += 1;
    } else if (outcome === 'renamed') {
      imported += 1;
      renamed += 1;
    } else {
      skipped += 1;
    }
    await reportProgress(
      onProgress,
      {
        completed: progressOffset + index + 1,
        total,
        currentFile,
      },
      yieldState,
      { force: index === entries.length - 1 },
    );
  }

  return { imported, skipped, renamed, processed: entries.length };
}

/**
 * True when the browser folder picker populated `webkitRelativePath` (a
 * recursive listing of every file under the chosen directory).
 */
export function isFolderTreeFile(file: File): boolean {
  const relativePath = (file as File & { webkitRelativePath?: string })
    .webkitRelativePath;
  return typeof relativePath === 'string' && relativePath !== '';
}

function folderTreeRelativePath(file: File): string | undefined {
  if (!isFolderTreeFile(file)) {
    return undefined;
  }
  return (file as File & { webkitRelativePath?: string }).webkitRelativePath;
}

/**
 * Import markdown files, a zip archive, or a folder tree (via webkitRelativePath)
 * into an existing workspace. Existing paths are never overwritten.
 *
 * Folder picks walk the entire selected tree (every nested folder). Only
 * markdown-like notes are imported; their relative paths are preserved so the
 * workspace mirrors the source folder structure.
 */
export async function importFilesToWorkspace({
  wsName,
  files,
  fileSystem,
  signal,
  onProgress,
}: {
  wsName: string;
  files: readonly File[];
  fileSystem: WorkspaceImportExportFileSystem;
  signal?: AbortSignal;
  onProgress?: ImportProgressCallback;
}): Promise<ImportWorkspaceResult> {
  let imported = 0;
  let skipped = 0;
  let renamed = 0;

  const list = Array.from(files);
  const looksLikeFolderTree = list.some(isFolderTreeFile);
  let completed = 0;
  let total = list.length;
  const progressYield = { lastYieldAt: 0 };
  await reportProgress(onProgress, { completed, total }, progressYield, {
    force: true,
  });

  for (let index = 0; index < list.length; index += 1) {
    const file = list[index];
    if (!file) {
      continue;
    }
    if (signal?.aborted) {
      throw signal.reason ?? new Error('Import aborted');
    }

    if (isZipFile(file) && !looksLikeFolderTree) {
      const remainingAfter = list.length - index - 1;
      const zipResult = await importZipFile({
        wsName,
        file,
        fileSystem,
        signal,
        onProgress,
        progressOffset: completed,
        progressRemainingAfter: remainingAfter,
        progressYield,
      });
      imported += zipResult.imported;
      skipped += zipResult.skipped;
      renamed += zipResult.renamed;
      completed += Math.max(zipResult.processed, 1);
      total = completed + remainingAfter;
      continue;
    }

    const relativeFromFolder = folderTreeRelativePath(file);
    const currentFile =
      (looksLikeFolderTree && relativeFromFolder) ||
      fileNameOf(file) ||
      undefined;
    const isLast = index === list.length - 1;

    // Folder trees: keep the relative path (including nested directories) and
    // only take markdown notes — skip assets, zips, and other non-notes.
    if (looksLikeFolderTree) {
      await reportProgress(
        onProgress,
        { completed, total, currentFile },
        progressYield,
      );
      if (!relativeFromFolder || !isMarkdownFile(file)) {
        skipped += 1;
        completed += 1;
        await reportProgress(
          onProgress,
          { completed, total, currentFile },
          progressYield,
          { force: isLast },
        );
        continue;
      }

      const outcome = await importRelativeEntry({
        wsName,
        relativePath: relativeFromFolder,
        file,
        fileSystem,
      });
      if (outcome === 'imported') {
        imported += 1;
      } else if (outcome === 'renamed') {
        imported += 1;
        renamed += 1;
      } else {
        skipped += 1;
      }
      completed += 1;
      await reportProgress(
        onProgress,
        { completed, total, currentFile },
        progressYield,
        { force: isLast },
      );
      continue;
    }

    await reportProgress(
      onProgress,
      { completed, total, currentFile },
      progressYield,
    );
    if (!isMarkdownFile(file)) {
      skipped += 1;
      completed += 1;
      await reportProgress(
        onProgress,
        { completed, total, currentFile },
        progressYield,
        { force: isLast },
      );
      continue;
    }

    const outcome = await importRelativeEntry({
      wsName,
      relativePath: file.name,
      file,
      fileSystem,
    });
    if (outcome === 'imported') {
      imported += 1;
    } else if (outcome === 'renamed') {
      imported += 1;
      renamed += 1;
    } else {
      skipped += 1;
    }
    completed += 1;
    await reportProgress(
      onProgress,
      { completed, total, currentFile },
      progressYield,
      { force: isLast },
    );
  }

  return { imported, skipped, renamed };
}
